"use client";

import { useState, useTransition } from "react";
import { listVersions, restoreVersion } from "../actions";
import type { VersionInfo } from "../types";
import { BTN_GHOST, BTN_PRIMARY, ConfirmButton, Notice, PageTitle } from "./ui";

/** The parts of the site that can be put back (src/server/application/history.ts RESTORABLE). */
export const HISTORY_PARTS = [
  { value: "posts", label: "Blog" },
  { value: "projects", label: "Projects" },
  { value: "services", label: "Services" },
  { value: "faq", label: "FAQ" },
  { value: "pages", label: "Page text & settings" },
  { value: "ui", label: "Interface text" },
  { value: "launch", label: "Coming-soon switch" },
] as const;
type Part = (typeof HISTORY_PARTS)[number]["value"];

const when = (iso: string) =>
  iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "Unknown time";
const kb = (bytes: number) => (bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`);

/** Earlier versions of each part of the site, newest first, with a button to put one back. */
export function HistoryBrowser({ initialPart, initialVersions }: { initialPart: Part; initialVersions: VersionInfo[] }) {
  const [part, setPart] = useState<Part>(initialPart);
  const [versions, setVersions] = useState(initialVersions);
  const [notice, setNotice] = useState<{ type: "ok" | "error"; text: string } | null>(null);
  const [pending, startTransition] = useTransition();

  const show = (next: Part) =>
    startTransition(async () => {
      setPart(next);
      setNotice(null);
      setVersions(await listVersions(next));
    });

  const restore = (v: VersionInfo) =>
    startTransition(async () => {
      const result = await restoreVersion(part, v.id);
      setNotice(result.ok ? { type: "ok", text: `Restored the version from ${when(v.savedAt)}. The one it replaced is now at the top of the list.` } : { type: "error", text: result.error });
      setVersions(await listVersions(part));
    });

  const label = HISTORY_PARTS.find((p) => p.value === part)!.label;

  return (
    <div aria-busy={pending}>
      <PageTitle title="History" />
      <p className="-mt-3 mb-5 text-midnight/70">
        Every save keeps the version it replaced (the last 30 for each part). Restoring puts that version back on the website; the version it replaces is kept too, so you can
        undo a restore here. Leads and media aren&apos;t restored this way.
      </p>
      <div role="tablist" aria-label="Part of the site" className="mb-4 flex flex-wrap gap-2">
        {HISTORY_PARTS.map((p) => (
          <button
            key={p.value}
            type="button"
            role="tab"
            aria-selected={part === p.value}
            onClick={() => show(p.value)}
            className={`${BTN_GHOST} ${part === p.value ? "border-midnight bg-midnight text-offwhite hover:text-offwhite" : ""}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      <Notice notice={notice} />
      {versions.length ? (
        <ol className="mt-4 flex flex-col gap-3">
          {versions.map((v, i) => (
            <li key={v.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-midnight/10 bg-sand p-4 sm:p-5">
              <div className="min-w-0">
                <p className="font-bold">{when(v.savedAt)}</p>
                <p className="font-mono text-xs text-midnight/70">
                  {i === 0 ? "The version before the latest save" : "Earlier version"} · {kb(v.size)}
                  {v.source === "import" ? " · moved from the old server" : ""}
                </p>
              </div>
              <ConfirmButton
                label="Restore"
                className={BTN_GHOST}
                question={`Put this ${label.toLowerCase()} version back?`}
                confirmLabel="Yes, restore"
                confirmClassName={BTN_PRIMARY}
                onConfirm={() => restore(v)}
              />
            </li>
          ))}
        </ol>
      ) : (
        <p className="mt-4 rounded-2xl border border-dashed border-midnight/20 p-8 text-center text-midnight/70">
          No earlier versions of {label.toLowerCase()} yet. One is kept each time it is saved.
        </p>
      )}
    </div>
  );
}
